
import React, { useState, useMemo } from 'react';
import { Appointment, Employee } from '../types';
import { Button } from './Button';
import type { AppointmentModalData } from '../types';

interface AppointmentsPageProps {
  appointments: Appointment[];
  employees: Employee[];
  onOpenModal: (data?: AppointmentModalData) => void;
}

// Icons
const CalendarIconSolid: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 mr-2 text-[var(--color-primary)]" {...props}>
    <path d="M12.75 12.75a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" />
    <path fillRule="evenodd" d="M3.75 4.5a.75.75 0 01.75-.75h.75c.31 0 .622.066.9.195A2.251 2.251 0 018.25 3h7.5c.646 0 1.227.254 1.655.695.428.441.656.99.695 1.573V6.75A.75.75 0 0118 6h2.25a.75.75 0 01.75.75v11.25a3 3 0 01-3 3H6a3 3 0 01-3-3V6.75A.75.75 0 013.75 6H6a.75.75 0 01-.75-.75V4.5zM6 6.75v.75c0 .31-.066.622-.195.9A2.251 2.251 0 003 10.5H1.5V6.75c0-.646.254-1.227.695-1.655C2.637 4.668 3.186 4.44 3.75 4.305V6.75zM20.25 6.75H18A.75.75 0 0117.25 6V4.305c.564.135 1.113.366 1.555.806.441.428.695.99.695 1.655V6.75z" clipRule="evenodd" />
  </svg>
);
const ChevronLeftIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" /></svg>
);
const ChevronRightIcon: React.FC<React.SVGProps<SVGSVGElement>> = (props) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5" {...props}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" /></svg>
);

export const AppointmentsPage: React.FC<AppointmentsPageProps> = ({ appointments, employees, onOpenModal }) => {
  const [currentDate, setCurrentDate] = useState(new Date());

  const daysInMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return new Date(year, month + 1, 0).getDate();
  }, [currentDate]);

  const firstDayOfMonth = useMemo(() => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    return new Date(year, month, 1).getDay(); // 0 for Sunday, 1 for Monday, etc.
  }, [currentDate]);

  const monthName = useMemo(() => {
    return currentDate.toLocaleString('default', { month: 'long', year: 'numeric' });
  }, [currentDate]);

  const appointmentsByDate = useMemo(() => {
    const map = new Map<string, Appointment[]>();
    appointments.forEach(apt => {
      const dateKey = apt.date; // YYYY-MM-DD
      if (!map.has(dateKey)) {
        map.set(dateKey, []);
      }
      map.get(dateKey)?.push(apt);
    });
    // Sort appointments within each day by start time
    map.forEach(dayAppointments => {
      dayAppointments.sort((a, b) => a.startTime.localeCompare(b.startTime));
    });
    return map;
  }, [appointments]);

  const handlePrevMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const handleNextMonth = () => {
    setCurrentDate(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };
  
  const handleToday = () => {
    setCurrentDate(new Date());
  };

  const getTechnicianName = (id?: string) => employees.find(e => e.id === id)?.name || 'Unassigned';

  const renderCalendarDays = () => {
    const days = [];
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(<div key={`empty-start-${i}`} className="border border-slate-200 bg-slate-50 h-32"></div>);
    }

    // Add days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const dayAppointments = appointmentsByDate.get(dateKey) || [];
      const isToday = new Date().toDateString() === new Date(currentDate.getFullYear(), currentDate.getMonth(), day).toDateString();

      days.push(
        <div key={day} className={`border border-slate-200 p-2 h-36 overflow-y-auto ${isToday ? 'bg-sky-50' : 'bg-white'} hover:bg-slate-50 transition-colors`}>
          <div className={`font-semibold text-sm ${isToday ? 'text-sky-600' : 'text-slate-700'}`}>{day}</div>
          {dayAppointments.map(apt => (
            <div
              key={apt.id}
              onClick={() => onOpenModal({ appointment: apt })}
              className="text-xs bg-[var(--color-primary)] bg-opacity-10 text-[var(--color-primary)] p-1 rounded mt-1 cursor-pointer hover:bg-opacity-20"
              title={`Edit: ${apt.serviceDescription}`}
            >
              <p className="font-semibold truncate">{apt.startTime} - {apt.serviceDescription}</p>
              <p className="truncate">{apt.customerName}</p>
              <p className="truncate text-slate-600 text-[10px]">Tech: {getTechnicianName(apt.assignedTechnicianId)}</p>
            </div>
          ))}
           {dayAppointments.length === 0 && (
            <button 
                onClick={() => onOpenModal({ date: dateKey })}
                className="mt-2 text-xs text-slate-400 hover:text-[var(--color-primary)] w-full text-left"
                aria-label={`Book new appointment for ${monthName} ${day}`}
            >
                + Book
            </button>
          )}
        </div>
      );
    }
    // Add empty cells for days after the last day of the month to complete the grid
    const totalCells = days.length;
    const remainingCells = (7 - (totalCells % 7)) % 7;
    for (let i = 0; i < remainingCells; i++) {
        days.push(<div key={`empty-end-${i}`} className="border border-slate-200 bg-slate-50 h-32"></div>);
    }

    return days;
  };
  
  const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className="space-y-6">
      <div className="bg-white p-6 rounded-lg shadow-md">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4">
          <div className="flex items-center mb-3 sm:mb-0">
            <CalendarIconSolid />
            <h1 className="text-2xl font-bold text-[var(--color-primary)]">Appointments Calendar</h1>
          </div>
          <Button variant="primary" onClick={() => onOpenModal({})}>
            Book New Appointment
          </Button>
        </div>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Button variant="ghost" onClick={handlePrevMonth} aria-label="Previous month">
              <ChevronLeftIcon />
            </Button>
            <Button variant="ghost" onClick={handleToday} className="text-sm px-3 py-1.5">Today</Button>
            <Button variant="ghost" onClick={handleNextMonth} aria-label="Next month">
              <ChevronRightIcon />
            </Button>
          </div>
          <h2 className="text-xl font-semibold text-slate-700">{monthName}</h2>
        </div>
        
        <div className="grid grid-cols-7 gap-px border border-slate-200 bg-slate-200 rounded-md overflow-hidden">
          {weekdays.map(day => (
            <div key={day} className="bg-slate-100 p-2 text-center text-xs font-medium text-slate-600">{day}</div>
          ))}
          {renderCalendarDays()}
        </div>
      </div>
    </div>
  );
};
